/*
 * Establishes a control-plane session for a signed-in Backstage user, once on app load.
 *
 * WHY THIS EXISTS
 *
 * TechDocs static content is served by nginx and gated by an `auth_request` to the control plane
 * (ADR 0029), which reads the `stackschematic_session` cookie. A user signed in to Backstage
 * (Google) does NOT automatically hold that cookie: the control plane is a separate session system,
 * bridged by FEDERATED SIGN-IN — exchanging Backstage's signed identity token for a StackSchematic
 * session. The admin surfaces (Agents, Guide admin, Uptime admin) already trigger that exchange when
 * visited, but the docs page is Backstage's own and triggers nothing. So this runs the exchange up
 * front, once, so a signed-in user has the session by the time they open the docs — no visible
 * second sign-in. As a side effect it is the "join the two logins" step: every signed-in Backstage
 * user becomes a signed-in control-plane user (a `viewer`, per the federated-sign-in rules — no new
 * authority is granted).
 *
 * BEST-EFFORT AND INVISIBLE
 *
 * Renders nothing, never reloads the page, and swallows every error — a failure just leaves the docs
 * gate unsatisfied, which is no worse than not running at all. A guest sign-in has no verified token,
 * so it is a no-op.
 *
 * The exchange is a single POST to /auth/host. The canonical client for it is
 * overlays/backstage/fleet/controlPlane.ts (ControlPlaneClient#signIn, which adds interactive
 * error handling and a reload-on-stale-token recovery). It is INLINED here rather than imported so
 * this app-level module stays self-contained and does not couple to the fleet page's module — and
 * because a background establish deliberately wants none of that interactive recovery (no reloads).
 */
import { AppRootElementBlueprint } from '@backstage/frontend-plugin-api';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useEffect } from 'react';

function EstablishControlPlaneSession(): null {
  const config = useApi(configApiRef);
  const identity = useApi(identityApiRef);
  const controlPlaneUrl =
    config.getOptionalString('stackschematic.controlPlaneUrl') ?? window.location.origin;

  useEffect(() => {
    let cancelled = false;
    identity
      .getCredentials()
      .then(({ token }) => {
        // No token = guest sign-in, nothing to exchange. cancelled = unmounted before the token
        // resolved.
        if (cancelled || token === undefined) return;
        void fetch(`${controlPlaneUrl.replace(/\/$/, '')}/auth/host`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          // The exchange sets an HttpOnly `stackschematic_session` cookie on this origin; every
          // later request (including the docs' nginx-gated static assets) rides it.
          credentials: 'include',
          body: JSON.stringify({ token }),
        }).catch(() => {
          // Silent: a failed background establish leaves the docs gate unmet, no worse than before.
        });
      })
      .catch(() => {
        // getCredentials can reject during sign-in transitions; ignore.
      });
    return () => {
      cancelled = true;
    };
  }, [identity, controlPlaneUrl]);

  return null;
}

export const controlPlaneSession = AppRootElementBlueprint.make({
  name: 'stackschematic-control-plane-session',
  params: { element: <EstablishControlPlaneSession /> },
});
